import { Component, effect, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { LoginFacade } from '../../../state/login.facade';
import { ConfigService } from '../../../../services/config.service';
import { RecaptchaComponent } from 'ng-recaptcha';
import { toSignal } from '@angular/core/rxjs-interop';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { LoginOption } from '../../../../models/client-configuration';
import { FormBuilderService } from '../../../../services/form-builder.service';
import { Subscription } from 'rxjs';
import { FingerprintService } from '../../../../services/fingerprint.service';
import { ActivatedRoute, Router } from '@angular/router';
import { openWindow } from '../../../../_core/utils/open-window';
import { MatLegacyDialogRef } from '@angular/material/legacy-dialog';
import { LoginDialogComponent } from '../../login-dialog/login-dialog.component';
import { ToastService } from '../../../../_core/services/toast.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  private static MAX_LOGIN_ATTEMPTS = 3;

  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly loginFacade = inject(LoginFacade);
  private readonly configService = inject(ConfigService);
  private readonly formBuilderService = inject(FormBuilderService);
  private readonly fingerprintService = inject(FingerprintService);
  private readonly router = inject(Router);
  private readonly dialogRef = inject(MatLegacyDialogRef<LoginDialogComponent>);
  private readonly toastService = inject(ToastService);

  private librarySelectionSubscription: Subscription;
  private submitSubscription: Subscription;

  private readonly reCaptchaResponse = toSignal(this.loginFacade.reCaptchaResponse$);

  readonly allowSignup = signal<boolean>(this.configService.get().allowSignUp);
  readonly customMessage = signal<string>(this.configService.get().loginMessage);
  readonly loginAttempts = toSignal<number>(this.loginFacade.loginAttempts$);
  readonly externalUrl = signal<string>('');
  readonly maxLoginAttempts = signal<number>(LoginComponent.MAX_LOGIN_ATTEMPTS);
  readonly showForgotPassword = signal<boolean>(false);

  form: UntypedFormGroup = this.resetForm();
  selectedForm = 0;
  formModel: any = {};
  formFields: FormlyFieldConfig[][];
  formOptions: FormlyFormOptions = {};

  @ViewChild('captcha') reCaptchaEl!: RecaptchaComponent;

  constructor() {
    // Reset reCaptcha if, after verification, response is false.
    effect(() => {
      if (this.reCaptchaResponse() === false) {
        this.reCaptchaEl.reset();
      } else if (this.reCaptchaResponse() === true) {
        // Update form control to handle validity
        this.form.get('reCaptcha').setValue('valid_captcha');
      }
    });
  }

  ngOnInit() {
    this.initializeForm();
  }

  ngOnDestroy() {
    this.librarySelectionSubscription?.unsubscribe();
    this.submitSubscription?.unsubscribe();
  }

  loadForgotPassword(event: Event) {
    event.preventDefault();
    this.loginFacade.changeScreen('forgot-password');
  }

  loadSignup(event: Event) {
    event.preventDefault();
    this.loginFacade.changeLastScreen('login');
    this.loginFacade.changeScreen('signup');
  }

  async login(event?: Event) {
    event?.preventDefault();

    const visitorId = await this.fingerprintService.getVisitorId();

    // If there is a max connected devices, a visitorId must be created, otherwise fail trying to log in
    if (this.configService.get().deviceControlViaWeb && !visitorId) {
      this.toastService.showToast('login.unrecognisedBrowser', 'error');
      return;
    }

    if (this.form.invalid) {
      return;
    }

    if (this.externalUrl() !== '') {
      const afterLoginUrl = this.activatedRoute.snapshot.queryParamMap.get('afterLogin');

      if (afterLoginUrl) {
        sessionStorage.setItem('afterLogin', afterLoginUrl);
      }

      openWindow(this.externalUrl(), '_self');
      return;
    }

    this.loginFacade.login({
      identifier: this.formModel.loginId0.data,
      password: this.formModel.password,
      library: this.formModel.library,
      userType: this.formModel.userType,
      visitorId,
    }).subscribe({
      next: () => {
        this.dialogRef.close(true);
        this.loginFacade.closeDialog();
      },
      error: (error) => {
        this.form = this.resetForm();
        throw error;
      }
    });
  }

  resolvedCaptcha(captchaResponse: string) {
    this.loginFacade.verifyReCaptcha(captchaResponse);
  }

  errorCaptcha() {
    throw new Error(`reCAPTCHA error encountered`);
  }

  private initializeForm() {
    const defaultOptions = this.configService.get().loginOptions;
    let parameters: LoginOption[] = defaultOptions;

    if (this.configService.get().leisureMode) {
      parameters = [
        {
          fields: ['EMAIL'],
          integration: null,
        },
      ];
    } else if (this.configService.get().showSimplifiedLogin) {
      parameters = [
        {
          fields: this.getAllFieldsAcrossOptions([defaultOptions[0]]),
          integration: defaultOptions[0].integration,
        }
      ];
    }

    if (this.configService.isIberoamericana()) {
      parameters[0].fields.unshift('USERTYPE');
    }

    // if (parameters) - Should be true always, even in the old code
    this.formFields = this.formBuilderService.parse(parameters);
    if (this.configService.isClientDibam()) {
      this.refreshLibraryParams(0, parameters);
    } else {
      this.refreshLibraryParams(parameters[0].library, parameters);
    }

    this.librarySelectionSubscription = this.formBuilderService.loginLibrarySelect$.subscribe((field) => {
      this.refreshLibraryParams(field.value, parameters);
    });

    this.submitSubscription = this.formBuilderService.loginSubmit$.subscribe(() => {
      this.login();
    });
  }

  private getAllFieldsAcrossOptions(options: LoginOption[]): string[] {
    const fieldsWithoutDuplicates = new Set(options.flatMap((option) => option.fields));
    return Array.from(fieldsWithoutDuplicates);
  }

  private refreshLibraryParams(library: string | number, parameters: LoginOption[]) {
    if (this.configService.isClientDibam()) {
      this.selectedForm = library as number;
    } else {
      this.selectedForm = parameters.findIndex((f) => f.library === library as string);
    }

    const urlParam = parameters[this.selectedForm].fields[0];
    const splitter = 'EXTERNAL_URL=';

    this.externalUrl.set(urlParam.startsWith(splitter) ? urlParam.split(splitter)[1] : '');
    this.form = this.resetForm();

    this.showForgotPassword.set(this.configService.get().showForgotPassword || parameters[this.selectedForm].forgotPasswordEnabled);
  }

  private resetForm(): UntypedFormGroup {
    if (this.loginAttempts() < LoginComponent.MAX_LOGIN_ATTEMPTS) {
      return new UntypedFormGroup({});
    }

    return new UntypedFormGroup({
      reCaptcha: new UntypedFormControl(null, Validators.required),
    });
  }
}
